import { PRESET_TYPE } from 'configs/meeting';

/**
 * Generic participant type for helper functions
 * Works with both DyteParticipant and IMeetingParticipant
 */
interface ParticipantLike {
  customParticipantId?: string;
  id?: string;
  presetName?: string;
}

/**
 * Checks if a participant is a tutor based on their preset name
 * @param presetName - The preset name of the participant
 * @returns true if the participant is a tutor, false otherwise
 */
export const isTutorParticipant = (presetName: string | undefined): boolean => {
  return presetName === PRESET_TYPE.TUTOR || presetName === 'group_call_host';
};

/**
 * Extracts the user ID from a participant ID
 *
 * Expected format: participantId is in the format "prefix-userId" or just "userId"
 * Examples: "session-12345" -> "12345", "user-abc" -> "abc"
 *
 * @param participantId - The participant ID (customParticipantId or id)
 * @returns The extracted user ID, or undefined if extraction fails
 */
export const extractUserIdFromParticipantId = (
  participantId: string | undefined,
): string | undefined => {
  if (!participantId) return undefined;

  // Participant ID format: "prefix-userId" or "userId"
  // Extract the last segment after splitting by '-'
  const parts = participantId.split('-');
  return parts.length > 0 ? parts[parts.length - 1] : participantId;
};

/**
 * Checks if a participant should be included for parent source filtering
 * Only tutor and the selected student should be included
 *
 * @param participant - The participant object (works with both Dyte and IMeetingParticipant)
 * @param studentId - The ID of the student that should be included
 * @returns true if participant should be included (tutor or selected student), false otherwise
 */
export const shouldIncludeAudioForParent = (
  participant: ParticipantLike,
  studentId: string | null | undefined,
): boolean => {
  if (isTutorParticipant(participant.presetName)) {
    return true;
  }

  if (!studentId) return false;

  const participantId = participant?.customParticipantId || participant?.id;
  if (!participantId) return false;

  const userId = extractUserIdFromParticipantId(participantId);
  return userId === studentId;
};

/**
 * Checks if a participant should be shown for parent source filtering
 * Only tutor and the selected student should be shown
 *
 * @param participant - The participant object (works with both Dyte and IMeetingParticipant)
 * @param studentId - The ID of the student that should be shown
 * @returns true if participant should be shown (tutor or selected student), false otherwise
 */
export const shouldShowForParent = (
  participant: ParticipantLike,
  studentId: string | null | undefined,
): boolean => {
  if (isTutorParticipant(participant.presetName)) {
    return true;
  }

  if (!studentId) return false;

  const participantId = participant?.customParticipantId || participant?.id;
  if (!participantId) return false;

  const userId = extractUserIdFromParticipantId(participantId);
  return userId === studentId;
};
